import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const values = new FormData(e.currentTarget);
    const submittedPassword = String(values.get("password") || pw);
    const submittedConfirmation = String(values.get("confirm_password") || pw2);
    if (submittedPassword.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (submittedPassword !== submittedConfirmation) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: submittedPassword, confirm_password: submittedConfirmation });
      toast.success("Password reset. Sign in with your new password.");
      navigate("/signin", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reset password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 grid place-items-center p-6">
      <div className="w-full max-w-md">
        <Link to="/signin" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-300">
          <ArrowLeft size={14}/> Back to sign in
        </Link>
        <div className="mt-6">
          <div className="label-eyebrow">New password</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Set a new password</h1>
          <p className="text-sm text-zinc-400 mt-2">Choose a password at least 8 characters long. This will sign you out of all other sessions.</p>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16}/>
            <input
              data-testid="reset-password"
              name="password" type={show ? "text" : "password"} autoComplete="new-password" required minLength={8}
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
              value={pw} onChange={(e) => setPw(e.target.value)}
              placeholder="New password"
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-10 pr-10 py-3 text-sm focus:outline-none focus:border-[#007AFF]"
            />
            <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
              {show ? <EyeOff size={14}/> : <Eye size={14}/>}
            </button>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16}/>
            <input
              data-testid="reset-confirm"
              name="confirm_password" type={show ? "text" : "password"} autoComplete="new-password" required minLength={8}
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
              value={pw2} onChange={(e) => setPw2(e.target.value)}
              placeholder="Confirm new password"
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-[#007AFF]"
            />
          </div>
          <button
            data-testid="reset-submit"
            type="submit" disabled={busy}
            className="w-full bg-[#007AFF] hover:bg-[#005ec4] text-white font-bold py-3 rounded-full disabled:opacity-50"
          >
            {busy ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}
